import BillingAPI from '../../api/billing';

const SET_BILLING_UI_FLAG = 'SET_BILLING_UI_FLAG';
const SET_BILLING_USAGE = 'SET_BILLING_USAGE';
const SET_BILLING_INVOICES = 'SET_BILLING_INVOICES';
const SET_BILLING_PLANS = 'SET_BILLING_PLANS';
const SET_BILLING_MAC_HISTORY = 'SET_BILLING_MAC_HISTORY';
const SET_BILLING_LLM_HISTORY = 'SET_BILLING_LLM_HISTORY';
const SET_BILLING_ADD_ONS = 'SET_BILLING_ADD_ONS';
const SET_BILLING_ADD_ON_PRODUCTS = 'SET_BILLING_ADD_ON_PRODUCTS';

const state = {
  usage: null,
  invoices: [],
  invoicesMeta: {},
  availablePlans: [],
  macHistory: null,
  llmUsageHistory: null,
  addOns: [],
  addOnProducts: [],
  uiFlags: {
    isFetching: false,
    isFetchingInvoices: false,
    isFetchingPlans: false,
    isFetchingMacHistory: false,
    isFetchingLlmUsageHistory: false,
    isChangingPlan: false,
    isCanceling: false,
    isReactivating: false,
    isPurchasingAddOn: false,
    isSyncingInvoices: false,
  },
};

export const getters = {
  // Core data getters
  currentPlan: $state => $state.usage?.plan || {},
  subscription: $state => $state.usage?.subscription || {},
  usage: $state => $state.usage?.usage || {},
  paymentMethod: $state => $state.usage?.payment_method || {},
  billingInfo: $state => $state.usage?.billing_info || {},
  paymentGrace: $state => $state.usage?.payment_grace || {},
  invoices: $state => $state.invoices,
  invoicesMeta: $state => $state.invoicesMeta,
  availablePlans: $state => $state.availablePlans,
  macHistory: $state => $state.macHistory,
  llmUsageHistory: $state => $state.llmUsageHistory,
  addOns: $state => $state.usage?.add_ons || $state.addOns || [],
  addOnProducts: $state => $state.addOnProducts || [],
  uiFlags: $state => $state.uiFlags,

  // Usage sub-getters for banner component
  macUsage: $state => $state.usage?.usage?.mac || {},
  llmTokenUsage: $state => $state.usage?.usage?.llm_tokens || {},

  // Limit flags
  isMacLimitReached: $state => $state.usage?.usage?.mac?.limit_reached || false,
  isLlmLimitReached: $state =>
    $state.usage?.usage?.llm_tokens?.limit_reached || false,

  // Usage percentages
  macUsagePercent: $state => {
    const mac = $state.usage?.usage?.mac;
    if (!mac || !mac.effective_limit) return 0;
    return Math.round((mac.used / mac.effective_limit) * 100);
  },
  llmTokenUsagePercent: $state => {
    const llm = $state.usage?.usage?.llm_tokens;
    if (!llm || !llm.effective_limit) return 0;
    return Math.round((llm.used / llm.effective_limit) * 100);
  },
  userUsagePercent: $state => {
    const users = $state.usage?.usage?.users;
    if (!users || !users.effective_limit) return 0;
    return Math.round((users.used / users.effective_limit) * 100);
  },

  // Status helpers
  subscriptionStatus: $state => $state.usage?.subscription?.status,
  isTrialing: $state => $state.usage?.subscription?.status === 'trial',
  isCanceling: $state => $state.usage?.subscription?.status === 'canceling',
  activeAddOns: $state => $state.addOns.filter(a => a.status === 'active'),
  chargesViaStripe: $state => Boolean($state.usage?.charges_via_stripe),
  stripeBillingPending: $state => Boolean($state.usage?.stripe_billing_pending),
};

export const actions = {
  fetchBillingUsage: async ({ commit }) => {
    commit(SET_BILLING_UI_FLAG, { isFetching: true });
    try {
      const { data } = await BillingAPI.getUsage();
      commit(SET_BILLING_USAGE, data);
    } catch (error) {
      throw error;
    } finally {
      commit(SET_BILLING_UI_FLAG, { isFetching: false });
    }
  },

  fetchInvoices: async ({ commit }, { page = 1, perPage = 20 } = {}) => {
    commit(SET_BILLING_UI_FLAG, { isFetchingInvoices: true });
    try {
      const { data } = await BillingAPI.getInvoices(page, perPage);
      commit(SET_BILLING_INVOICES, data);
    } catch (error) {
      // silently fail
    } finally {
      commit(SET_BILLING_UI_FLAG, { isFetchingInvoices: false });
    }
  },

  fetchPlans: async ({ commit }) => {
    commit(SET_BILLING_UI_FLAG, { isFetchingPlans: true });
    try {
      const { data } = await BillingAPI.getPlans();
      commit(SET_BILLING_PLANS, data.plans);
    } catch (error) {
      // silently fail
    } finally {
      commit(SET_BILLING_UI_FLAG, { isFetchingPlans: false });
    }
  },

  changePlan: async ({ commit, dispatch }, { planId, billingCycle }) => {
    commit(SET_BILLING_UI_FLAG, { isChangingPlan: true });
    try {
      await BillingAPI.changePlan(planId, billingCycle);
      await dispatch('fetchBillingUsage');
    } finally {
      commit(SET_BILLING_UI_FLAG, { isChangingPlan: false });
    }
  },

  cancelSubscription: async (
    { commit, dispatch },
    { immediate = false, reason = '' } = {}
  ) => {
    commit(SET_BILLING_UI_FLAG, { isCanceling: true });
    try {
      await BillingAPI.cancelSubscription(immediate, reason);
      await dispatch('fetchBillingUsage');
    } finally {
      commit(SET_BILLING_UI_FLAG, { isCanceling: false });
    }
  },

  reactivate: async ({ commit, dispatch }) => {
    commit(SET_BILLING_UI_FLAG, { isReactivating: true });
    try {
      await BillingAPI.reactivate();
      await dispatch('fetchBillingUsage');
    } finally {
      commit(SET_BILLING_UI_FLAG, { isReactivating: false });
    }
  },

  managePayment: async () => {
    const { data } = await BillingAPI.managePayment();
    return data;
  },

  manageBillingInfo: async () => {
    const { data } = await BillingAPI.manageBillingInfo();
    return data;
  },

  fetchMacHistory: async (
    { commit },
    { periodStart, periodEnd, inboxId } = {}
  ) => {
    commit(SET_BILLING_UI_FLAG, { isFetchingMacHistory: true });
    try {
      const { data } = await BillingAPI.getMacHistory(
        periodStart,
        periodEnd,
        inboxId
      );
      commit(SET_BILLING_MAC_HISTORY, data);
    } catch (error) {
      // silently fail
    } finally {
      commit(SET_BILLING_UI_FLAG, { isFetchingMacHistory: false });
    }
  },

  fetchLlmUsageHistory: async (
    { commit },
    { periodStart, periodEnd, feature, model } = {}
  ) => {
    commit(SET_BILLING_UI_FLAG, { isFetchingLlmUsageHistory: true });
    try {
      const { data } = await BillingAPI.getLlmUsageHistory(
        periodStart,
        periodEnd,
        feature,
        model
      );
      commit(SET_BILLING_LLM_HISTORY, data);
    } catch (error) {
      // silently fail
    } finally {
      commit(SET_BILLING_UI_FLAG, { isFetchingLlmUsageHistory: false });
    }
  },

  syncInvoices: async ({ commit, dispatch }) => {
    commit(SET_BILLING_UI_FLAG, { isSyncingInvoices: true });
    try {
      await BillingAPI.syncInvoices();
      await dispatch('fetchInvoices');
    } finally {
      commit(SET_BILLING_UI_FLAG, { isSyncingInvoices: false });
    }
  },

  purchaseAddOn: async ({ commit, dispatch }, { productId, quantity }) => {
    commit(SET_BILLING_UI_FLAG, { isPurchasingAddOn: true });
    try {
      await BillingAPI.purchaseAddOn(productId, quantity);
      await dispatch('fetchBillingUsage');
      await dispatch('fetchAddOns');
    } finally {
      commit(SET_BILLING_UI_FLAG, { isPurchasingAddOn: false });
    }
  },

  fetchAddOns: async ({ commit }) => {
    try {
      const { data } = await BillingAPI.getAddOns();
      commit(SET_BILLING_ADD_ONS, data.add_ons);
    } catch (error) {
      // silently fail
    }
  },

  fetchAddOnProducts: async ({ commit }) => {
    try {
      const { data } = await BillingAPI.getAddOnProducts();
      commit(SET_BILLING_ADD_ON_PRODUCTS, data.products);
    } catch (error) {
      // silently fail
    }
  },

  cancelAddOn: async ({ dispatch }, addOnId) => {
    await BillingAPI.cancelAddOn(addOnId);
    await dispatch('fetchBillingUsage');
    await dispatch('fetchAddOns');
  },
};

export const mutations = {
  [SET_BILLING_UI_FLAG]($state, data) {
    $state.uiFlags = { ...$state.uiFlags, ...data };
  },
  [SET_BILLING_USAGE]($state, data) {
    $state.usage = data;
  },
  [SET_BILLING_INVOICES]($state, data) {
    $state.invoices = data.invoices || [];
    $state.invoicesMeta = data.meta || {};
  },
  [SET_BILLING_PLANS]($state, plans) {
    $state.availablePlans = plans || [];
  },
  [SET_BILLING_MAC_HISTORY]($state, data) {
    $state.macHistory = data;
  },
  [SET_BILLING_LLM_HISTORY]($state, data) {
    $state.llmUsageHistory = data;
  },
  [SET_BILLING_ADD_ONS]($state, addOns) {
    $state.addOns = addOns || [];
  },
  [SET_BILLING_ADD_ON_PRODUCTS]($state, products) {
    $state.addOnProducts = products || [];
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
